import { Component, inject, DestroyRef, OnDestroy } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { AuthService } from './core/auth/auth.service';
import { AuthApiService } from './modules/auth';
import { interval, Subscription } from 'rxjs';
import { FuseLoadingService } from '@fuse/services/loading';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [RouterOutlet],
})
export class AppComponent implements OnDestroy {
    private readonly _authService = inject(AuthService);
    private readonly _authApiService = inject(AuthApiService);
    private readonly _router = inject(Router);
    private readonly _fuseLoadingService = inject(FuseLoadingService);

    
    private readonly TOKEN_CHECK_INTERVAL = 15000; // Check every 15 seconds
    private readonly TOKEN_REFRESH_THRESHOLD = 5 * 60; // 5 minutes in seconds
    private _intervalSubscription: Subscription;

    constructor() {
        this._setupTokenRefresh();
    }

    ngOnDestroy() {
        this._intervalSubscription?.unsubscribe();
    }

    private _setupTokenRefresh(): void {
        this._intervalSubscription = interval(this.TOKEN_CHECK_INTERVAL)
            .subscribe(() => {
                const shouldRefresh = this._shouldRefreshToken();
                
                if (shouldRefresh) {
                    this._refreshToken();
                }
            });
    }

    private _shouldRefreshToken(): boolean {
        if (!this._authService.accessToken || !this._authService.refreshToken) {
            return false;
        }

        if (this._router.url.includes('sign-in') || this._router.url.includes('sign-out')) {
            return false;
        }

        return this._authService.isTokenCloseToExpire;
    }

    private _refreshToken(): void {
        this._fuseLoadingService.setAutoMode(false);

        this._authApiService.renewToken(this._authService.refreshToken)
            .subscribe({
                next: (data) => {
                    this._authService.accessToken = data.accessToken;
                    this._authService.refreshToken = data.refreshToken;

                    this._fuseLoadingService.setAutoMode(false);
                },
                error: () => {
                    this._authService.signOut();
                    this._router.navigate(['/sign-in'], {
                        queryParams: { applicationId: this._authService.lastApplicationId },
                    });

                    this._fuseLoadingService.setAutoMode(false);
                }
            });
    }
}
